import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.js';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.ts';

/**
 * Ineligibility codes returned by the proxy API for Final gifts.
 * `''` (empty string) means eligible.
 */
export const FinalGiftIneligibilityCode = {
  notAvailable: 'FLG-Gift-Not-Available',
  alreadyGiven: 'FLG-Gift-Already-Given',
} as const;

export type GiftTypeInput =
  | GiftTypeEnum
  | CommitmentCodeEnum
  | string
  | null
  | undefined;

// UI gift type props should carry GiftTypeEnum values. Keep this compatibility
// normalization at adapter boundaries that still receive commitment/fund codes
// from API-shaped data or legacy stories.
export const normalizeGiftType = (
  giftType: GiftTypeInput,
): GiftTypeEnum | null => {
  switch (giftType) {
    case GiftTypeEnum.Birthday:
    case CommitmentCodeEnum.BirthdayGift: {
      return GiftTypeEnum.Birthday;
    }
    case GiftTypeEnum.Child:
    case CommitmentCodeEnum.ChildGift: {
      return GiftTypeEnum.Child;
    }
    case GiftTypeEnum.Family:
    case CommitmentCodeEnum.FamilyGift: {
      return GiftTypeEnum.Family;
    }
    case GiftTypeEnum.Center:
    case CommitmentCodeEnum.CenterGift: {
      return GiftTypeEnum.Center;
    }
    case GiftTypeEnum.Final:
    case CommitmentCodeEnum.FinalGift: {
      return GiftTypeEnum.Final;
    }
    default: {
      return null;
    }
  }
};

const getIsGiftTypeEnum =
  <T extends GiftTypeEnum>(giftEnumType: T) =>
  (giftType: GiftTypeInput): boolean =>
    normalizeGiftType(giftType) === giftEnumType;

export const isBirthdayGift = getIsGiftTypeEnum(GiftTypeEnum.Birthday);
export const isChildGift = getIsGiftTypeEnum(GiftTypeEnum.Child);
export const isFamilyGift = getIsGiftTypeEnum(GiftTypeEnum.Family);
export const isCenterGift = getIsGiftTypeEnum(GiftTypeEnum.Center);
export const isFinalGift = getIsGiftTypeEnum(GiftTypeEnum.Final);

/**
 * A child is eligible for a Final gift unless the proxy API explicitly marks
 * them as `FLG-Gift-Not-Available`. The `FLG-Gift-Already-Given` code still
 * counts as eligible for *display* — the card shows an informational state —
 * so it is not filtered out here.
 *
 * NOTE: Long-term this decision is expected to come from a "closed/exiting"
 * flag on the Sponsorship commitment in the commitments call. When that ships,
 * only this helper changes.
 */
export const isFinalChildEligible = (
  finalLimits: ChildGiftLimitItem | undefined,
): boolean =>
  !!finalLimits &&
  finalLimits.ineligibilityCode !== FinalGiftIneligibilityCode.notAvailable;
